import { html } from '@design.estate/dees-element';
import { demoApp } from '../../../demos/templates.js';
import type { DeesHarnessChat, IHarnessChatToolbarAction } from './dees-harness-chat.js';
import type { IHarnessMessage, IHarnessPermissionRequest, IHarnessToolCall } from '../interfaces.js';
import { harnessDemoConversation } from '../harness.demodata.js';

const toolShowcase = (): IHarnessMessage[] => {
  const now = Date.now();
  return [
    {
      id: 'demo-t1',
      role: 'tool',
      text: '',
      createdAt: now - 240_000,
      toolCall: {
        id: 'call-1',
        name: 'run_command',
        status: 'completed',
        input: { command: 'pnpm test --filter sitemap', cwd: '/srv/app' },
        output: { exitCode: 0, stdout: '14 tests passed' },
        startedAt: now - 250_000,
        finishedAt: now - 243_000,
      },
    },
    {
      id: 'demo-t2',
      role: 'tool',
      text: '',
      createdAt: now - 200_000,
      toolCall: {
        id: 'call-2',
        name: 'write_file',
        status: 'completed',
        input: {
          path: '/srv/app/src/jobs/sitemap.job.ts',
          oldContent: "const cacheKey = hash(locales);",
          content: "const cacheKey = hash(routeManifest);",
        },
        startedAt: now - 205_000,
        finishedAt: now - 202_000,
      },
    },
    {
      id: 'demo-t3',
      role: 'tool',
      text: '',
      createdAt: now - 150_000,
      toolCall: {
        id: 'call-3',
        name: 'kb__lookup_article',
        status: 'completed',
        mcp: {
          serverName: 'kb',
          toolName: 'lookup_article',
          annotations: { title: 'Knowledge Lookup', readOnlyHint: true, openWorldHint: true },
        },
        input: { query: 'sitemap cache determinism' },
        contentBlocks: [
          { type: 'text', text: 'Found “Deterministic sitemap builds”.' },
          {
            type: 'resource_link',
            uri: 'https://kb.example.com/articles/812',
            name: 'deterministic-sitemap-builds',
            title: 'Deterministic sitemap builds',
            mimeType: 'text/html',
          },
        ],
        startedAt: now - 155_000,
        finishedAt: now - 152_000,
      },
    },
    {
      id: 'demo-t4',
      role: 'assistant',
      text: 'Re-ran the suite to confirm the cache key fix.',
      createdAt: now - 140_000,
    },
    // A finished command that never reported an exit code: it collapses like any other clean
    // finish, alone in its row, so it shows the collapsed reading measure.
    {
      id: 'demo-t5',
      role: 'tool',
      text: '',
      createdAt: now - 130_000,
      toolCall: {
        id: 'call-5',
        name: 'run_command',
        status: 'completed',
        input: { command: 'pnpm test --filter sitemap', cwd: '/srv/app' },
        output: '14 tests passed\n0 failed',
        startedAt: now - 136_000,
        finishedAt: now - 131_000,
      },
    },
    {
      id: 'demo-t6',
      role: 'assistant',
      text: 'The lint run is still failing though.',
      createdAt: now - 120_000,
    },
    // The other half of the rule: a non-zero exit keeps its output on screen and the pane with it.
    {
      id: 'demo-t7',
      role: 'tool',
      text: '',
      createdAt: now - 110_000,
      toolCall: {
        id: 'call-7',
        name: 'run_command',
        status: 'completed',
        input: { command: 'pnpm lint', cwd: '/srv/app' },
        output: { exitCode: 1, stdout: '', stderr: 'sitemap.job.ts:12:3  error  Unexpected any' },
        startedAt: now - 116_000,
        finishedAt: now - 111_000,
      },
    },
  ];
};

const pendingPermission: IHarnessPermissionRequest = {
  id: 'perm-demo',
  title: 'Write file',
  type: 'write',
  createdAt: Date.now(),
  metadata: { path: '/srv/app/src/jobs/sitemap.job.ts', bytes: 812 },
};

interface ISubagentScenario {
  description: string;
  model: string;
  toolName: string;
  toolInput: Record<string, unknown>;
  toolOutput: unknown;
  resultChunks: string[];
  startDelay: number;
  toolDuration: number;
  chunkDelay: number;
}

const firstSubagentWave: ISubagentScenario[] = [
  {
    description: 'API contract',
    model: 'openai/gpt-5.5',
    toolName: 'read_file',
    toolInput: { path: '/srv/app/ts_interfaces/harness.ts', startLine: 1, endLine: 220 },
    toolOutput: 'IHarnessSessionProjection and event contracts loaded.',
    resultChunks: [
      'The projection boundary is already runtime-neutral. ',
      'Child ownership can stay on the session service. ',
      'Only bounded messages and status need to cross the API. ',
      'Contract review complete.',
    ],
    startDelay: 0,
    toolDuration: 1_300,
    chunkDelay: 620,
  },
  {
    description: 'Browser lifecycle',
    model: 'anthropic/claude-sonnet-4',
    toolName: 'browser',
    toolInput: { action: 'snapshot', url: 'http://localhost:3002/harness' },
    toolOutput: 'Captured active, reconnecting, and completed child-card states.',
    resultChunks: [
      'The nested transcript keeps its own scroll boundary. ',
      'Reconnect restores the content observer without duplicating it. ',
      'Completed cards return to chronology with DOM identity intact. ',
      'Browser lifecycle checks pass.',
    ],
    startDelay: 240,
    toolDuration: 1_750,
    chunkDelay: 700,
  },
  {
    description: 'Regression coverage',
    model: 'openai/gpt-5.5',
    toolName: 'run_command',
    toolInput: { command: 'pnpm exec tstest test/test.harness-subagent-streams.chromium.ts --web', cwd: '/srv/app' },
    toolOutput: { exitCode: 0, stdout: '11/11 focused Chromium tests passed' },
    resultChunks: [
      'Focused coverage exercises nested deltas and grid movement. ',
      'The projection-only drill-in path resolves correctly. ',
      'Native overscroll containment is present. ',
      'Regression coverage is green.',
    ],
    startDelay: 480,
    toolDuration: 2_200,
    chunkDelay: 760,
  },
];

const secondSubagentWave: ISubagentScenario[] = [
  {
    description: 'Authorization boundaries',
    model: 'openai/gpt-5.5',
    toolName: 'grep',
    toolInput: { pattern: 'requireSessionAccess', path: '/srv/app/ts' },
    toolOutput: '8 ownership checks found; all projection routes are guarded.',
    resultChunks: ['Checked route guards. ', 'Verified parent and child ownership. ', 'No cross-tenant projection path found. ', 'Authorization review complete.'],
    startDelay: 0,
    toolDuration: 1_350,
    chunkDelay: 620,
  },
  {
    description: 'Storage adapters',
    model: 'anthropic/claude-sonnet-4',
    toolName: 'read_file',
    toolInput: { path: '/srv/app/ts/classes.sessionstore.ts', startLine: 80, endLine: 260 },
    toolOutput: 'Session lookup and bounded transcript paging reviewed.',
    resultChunks: ['Read the paging path. ', 'Confirmed stable cursor ordering. ', 'Validated truncation metadata. ', 'Storage review complete.'],
    startDelay: 140,
    toolDuration: 1_700,
    chunkDelay: 680,
  },
  {
    description: 'RPC event mapping',
    model: 'openai/gpt-5.5',
    toolName: 'read_file',
    toolInput: { path: '/srv/app/ts_interfaces/events.ts', startLine: 1, endLine: 180 },
    toolOutput: 'Parent and nested stream event mapping is complete.',
    resultChunks: ['Mapped child status events. ', 'Mapped tool and reasoning deltas. ', 'Confirmed snapshot recovery after gaps. ', 'RPC event mapping complete.'],
    startDelay: 280,
    toolDuration: 1_150,
    chunkDelay: 740,
  },
  {
    description: 'Mobile layout',
    model: 'anthropic/claude-sonnet-4',
    toolName: 'browser',
    toolInput: { action: 'snapshot', url: 'http://localhost:3002/harness?viewport=phone' },
    toolOutput: 'Single-column cards and session overlay captured at 390px.',
    resultChunks: ['Reduced the transcript container. ', 'Confirmed one-column active cards. ', 'Opened the session overlay above the chat. ', 'Mobile layout is stable.'],
    startDelay: 420,
    toolDuration: 2_050,
    chunkDelay: 610,
  },
  {
    description: 'Reconnect behavior',
    model: 'openai/gpt-5.5',
    toolName: 'run_command',
    toolInput: { command: 'pnpm test --filter reconnect', cwd: '/srv/app' },
    toolOutput: { exitCode: 0, stdout: '6 reconnect scenarios passed' },
    resultChunks: ['Interrupted the child feed. ', 'Replayed the authoritative snapshot. ', 'Resumed deltas without duplication. ', 'Reconnect behavior passes.'],
    startDelay: 560,
    toolDuration: 2_400,
    chunkDelay: 720,
  },
  {
    description: 'Permission flow',
    model: 'anthropic/claude-sonnet-4',
    toolName: 'grep',
    toolInput: { pattern: 'harness-permission-response', path: '/srv/app/ts_web' },
    toolOutput: 'Permission responses remain child-session scoped.',
    resultChunks: ['Traced permission ownership. ', 'Confirmed answered cards persist. ', 'Verified nested requests never leak outward. ', 'Permission flow is correct.'],
    startDelay: 700,
    toolDuration: 1_500,
    chunkDelay: 790,
  },
  {
    description: 'Documentation audit',
    model: 'openai/gpt-5.5',
    toolName: 'read_file',
    toolInput: { path: '/srv/app/readme.md', startLine: 1800, endLine: 1860 },
    toolOutput: 'Subtask projection, drill-in, and truncation behavior documented.',
    resultChunks: ['Compared docs with interfaces. ', 'Checked the projection example. ', 'Verified host responsibilities are explicit. ', 'Documentation audit complete.'],
    startDelay: 840,
    toolDuration: 1_900,
    chunkDelay: 650,
  },
  {
    description: 'Bundle impact',
    model: 'anthropic/claude-sonnet-4',
    toolName: 'run_command',
    toolInput: { command: 'pnpm run build', cwd: '/srv/app' },
    toolOutput: { exitCode: 0, stdout: 'TypeScript and browser bundle completed' },
    resultChunks: ['Compiled the component graph. ', 'Checked the browser bundle. ', 'Found no runtime-specific dependency. ', 'Bundle verification passes.'],
    startDelay: 980,
    toolDuration: 2_600,
    chunkDelay: 700,
  },
  {
    description: 'Release checklist',
    model: 'openai/gpt-5.5',
    toolName: 'webfetch',
    toolInput: { url: 'https://code.foss.global/design.estate/dees-catalog/raw/branch/main/changelog.md' },
    toolOutput: 'Release notes include bounded live subagent streams.',
    resultChunks: ['Reviewed the pending notes. ', 'Matched public API names. ', 'Confirmed verification results are recorded. ', 'Release checklist is ready.'],
    startDelay: 1_120,
    toolDuration: 2_150,
    chunkDelay: 760,
  },
];

const createSubagentWave = (
  runId: number,
  waveNumber: number,
  scenarios: ISubagentScenario[],
): IHarnessMessage[] => {
  const now = Date.now();
  return scenarios.map((scenario, index) => {
    const childSessionId = `demo-${runId}-wave-${waveNumber}-child-${index + 1}`;
    const childToolId = `${childSessionId}-tool`;
    return {
      id: `demo-${runId}-wave-${waveNumber}-subagent-${index + 1}`,
      role: 'tool' as const,
      text: '',
      createdAt: now + index,
      toolCall: {
        id: `demo-${runId}-wave-${waveNumber}-call-${index + 1}`,
        name: 'delegate',
        status: 'running' as const,
        input: { description: scenario.description },
        childSessionId,
        model: scenario.model,
        subtask: {
          sessionId: childSessionId,
          previewState: 'ready' as const,
          status: { type: 'busy' as const, message: `Investigating ${scenario.description.toLowerCase()}…` },
          messages: [
            {
              id: `${childSessionId}-plan`,
              role: 'assistant' as const,
              text: `I’ll inspect ${scenario.description.toLowerCase()} and report the concrete result.`,
              createdAt: now,
              markdown: false,
            },
            {
              id: childToolId,
              role: 'tool' as const,
              text: '',
              createdAt: now + 1,
              toolCall: {
                id: `${childToolId}-call`,
                name: scenario.toolName,
                status: 'running' as const,
                input: scenario.toolInput,
                startedAt: now,
              },
            },
            {
              id: `${childSessionId}-result`,
              role: 'assistant' as const,
              text: '',
              createdAt: now + 2,
              markdown: false,
              streaming: true,
            },
          ],
        },
        startedAt: now,
      },
    };
  });
};

let stopStream: (() => void) | undefined;

const chatOf = (event: MouseEvent): DeesHarnessChat | null =>
  (event.target as HTMLElement).closest('.chatDemo')?.querySelector('dees-harness-chat') ?? null;

const simulateSubagents = (event: MouseEvent): void => {
  const chat = chatOf(event);
  if (!chat) return;
  stopStream?.();
  const runId = Date.now();
  let stopped = false;
  let currentWaveLabel = '';
  const pendingDelays = new Map<ReturnType<typeof setTimeout>, (continueRun: boolean) => void>();
  const activeSubagents = new Map<string, IHarnessMessage>();
  const activeMainMessages = new Set<string>();
  const activeMainTools = new Set<string>();

  const wait = async (milliseconds: number): Promise<boolean> => new Promise((resolve) => {
    if (stopped) {
      resolve(false);
      return;
    }
    const timeout = setTimeout(() => {
      pendingDelays.delete(timeout);
      resolve(!stopped);
    }, milliseconds);
    pendingDelays.set(timeout, resolve);
  });
  const pushMessage = async (message: IHarnessMessage): Promise<boolean> => {
    chat.messages = [...chat.messages, message];
    await chat.updateComplete;
    return !stopped;
  };
  const updateWaveStatus = (): void => {
    if (stopped || !activeSubagents.size) return;
    chat.status = {
      type: 'busy',
      message: `${currentWaveLabel}: ${activeSubagents.size} subagent${activeSubagents.size === 1 ? '' : 's'} still working…`,
    };
  };
  const finishSubagent = (task: IHarnessMessage, status: 'completed' | 'error'): void => {
    if (!activeSubagents.has(task.id)) return;
    const call = task.toolCall;
    const subtask = call?.subtask;
    if (!call || subtask?.previewState !== 'ready') return;
    const now = Date.now();
    chat.applyDelta({
      type: 'tool-update',
      messageId: task.id,
      tool: {
        id: call.id,
        status,
        finishedAt: now,
        ...(status === 'error' ? { errorText: 'Simulation stopped.' } : {}),
        subtask: {
          ...subtask,
          status: status === 'completed' ? { type: 'idle' } : { type: 'error', message: 'Stopped' },
          messages: subtask.messages.map((message) => {
            if (message.toolCall?.status === 'pending' || message.toolCall?.status === 'running') {
              return {
                ...message,
                toolCall: {
                  ...message.toolCall,
                  status: status === 'completed' ? 'completed' as const : 'error' as const,
                  finishedAt: now,
                  ...(status === 'error' ? { errorText: 'Simulation stopped.' } : {}),
                },
              };
            }
            if (!message.streaming) return message;
            return {
              ...message,
              streaming: false,
              ...(status === 'error' ? { error: 'Simulation stopped.' } : {}),
            };
          }),
        },
      },
    });
    activeSubagents.delete(task.id);
    updateWaveStatus();
  };
  const runSubagent = async (task: IHarnessMessage, scenario: ISubagentScenario): Promise<void> => {
    const subtask = task.toolCall?.subtask;
    if (subtask?.previewState !== 'ready') return;
    const childTool = subtask.messages.find((message) => message.toolCall);
    const childResult = subtask.messages.find((message) => message.streaming);
    if (!childTool?.toolCall || !childResult) return;
    if (!await wait(scenario.startDelay)) return;
    if (!await wait(scenario.toolDuration)) return;
    chat.applyDelta({
      type: 'tool-update',
      parentMessageId: task.id,
      messageId: childTool.id,
      tool: {
        id: childTool.toolCall.id,
        status: 'completed',
        output: scenario.toolOutput,
        finishedAt: Date.now(),
      },
    });
    for (const chunk of scenario.resultChunks) {
      if (!await wait(scenario.chunkDelay)) return;
      chat.applyDelta({
        type: 'text',
        parentMessageId: task.id,
        messageId: childResult.id,
        delta: chunk,
      });
    }
    chat.applyDelta({
      type: 'message-end',
      parentMessageId: task.id,
      messageId: childResult.id,
    });
    finishSubagent(task, 'completed');
  };
  const runWave = async (
    waveNumber: number,
    label: string,
    scenarios: ISubagentScenario[],
  ): Promise<boolean> => {
    currentWaveLabel = label;
    const tasks = createSubagentWave(runId, waveNumber, scenarios);
    for (const task of tasks) activeSubagents.set(task.id, task);
    chat.messages = [...chat.messages, ...tasks];
    updateWaveStatus();
    await chat.updateComplete;
    await Promise.all(tasks.map((task, index) => runSubagent(task, scenarios[index])));
    return !stopped;
  };
  const streamMainMessage = async (text: string): Promise<boolean> => {
    const messageId = `demo-${runId}-main-${Date.now()}`;
    const message: IHarnessMessage = {
      id: messageId,
      role: 'assistant',
      text: '',
      createdAt: Date.now(),
      markdown: true,
      streaming: true,
      model: 'gpt-5.5',
    };
    activeMainMessages.add(messageId);
    if (!await pushMessage(message)) return false;
    for (let offset = 0; offset < text.length; offset += 18) {
      if (!await wait(70)) return false;
      chat.applyDelta({ type: 'text', messageId, delta: text.slice(offset, offset + 18) });
    }
    chat.applyDelta({ type: 'message-end', messageId });
    activeMainMessages.delete(messageId);
    return true;
  };

  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    for (const [timeout, resolve] of pendingDelays) {
      clearTimeout(timeout);
      resolve(false);
    }
    pendingDelays.clear();
    for (const task of [...activeSubagents.values()]) finishSubagent(task, 'error');
    for (const messageId of activeMainMessages) {
      chat.applyDelta({ type: 'message-end', messageId, error: 'Simulation stopped.' });
    }
    activeMainMessages.clear();
    for (const messageId of activeMainTools) {
      const tool = chat.messages.find((message) => message.id === messageId)?.toolCall;
      if (!tool) continue;
      chat.applyDelta({
        type: 'tool-update',
        messageId,
        tool: { id: tool.id, status: 'error', errorText: 'Simulation stopped.', finishedAt: Date.now() },
      });
    }
    activeMainTools.clear();
    chat.status = { type: 'idle' };
    if (stopStream === stop) stopStream = undefined;
  };
  stopStream = stop;

  const run = async (): Promise<void> => {
    chat.messages = [...harnessDemoConversation().slice(-2)];
    chat.permissions = [];
    chat.questions = [];
    if (!await runWave(1, 'First wave', firstSubagentWave)) return;

    chat.status = { type: 'busy', message: 'Main thread is reconciling the first wave…' };
    if (!await wait(520)) return;
    if (!await streamMainMessage(
      'The first three reviews agree on the bounded projection. I’m consolidating their findings before widening the verification pass.',
    )) return;

    const mainToolId = `demo-${runId}-main-checklist`;
    activeMainTools.add(mainToolId);
    if (!await pushMessage({
      id: mainToolId,
      role: 'tool',
      text: '',
      createdAt: Date.now(),
      toolCall: {
        id: `${mainToolId}-call`,
        name: 'todowrite',
        status: 'running',
        input: {
          todos: [
            { content: 'Reconcile initial contract findings', status: 'completed' },
            { content: 'Run nine parallel integration reviews', status: 'in_progress' },
            { content: 'Summarize release readiness', status: 'pending' },
          ],
        },
        startedAt: Date.now(),
      },
    })) return;
    if (!await wait(1_100)) return;
    chat.applyDelta({
      type: 'tool-update',
      messageId: mainToolId,
      tool: { id: `${mainToolId}-call`, status: 'completed', finishedAt: Date.now() },
    });
    activeMainTools.delete(mainToolId);
    if (!await streamMainMessage(
      'The core path is sound. I’m launching nine focused subagents across authorization, storage, RPC, responsive UI, reconnects, permissions, docs, bundling, and release readiness.',
    )) return;
    if (!await wait(600)) return;

    if (!await runWave(2, 'Integration wave', secondSubagentWave)) return;
    chat.status = { type: 'busy', message: 'Main thread is assembling the final result…' };
    if (!await wait(600)) return;
    if (!await streamMainMessage([
      'All **nine integration reviews** have completed at their own pace.',
      '',
      '- Authorization and permission boundaries remain session-scoped',
      '- Storage paging and reconnect recovery preserve bounded projections',
      '- Desktop and mobile layouts retain stable card ownership',
      '- Documentation, bundle, and release checks are ready',
      '',
      'The staged subagent run is complete.',
    ].join('\n'))) return;
    chat.status = { type: 'idle' };
    if (stopStream === stop) stopStream = undefined;
  };

  void run().catch((error) => {
    console.error('Harness subagent demo simulation failed.', error);
    stop();
  });
};

/**
 * Streams a realistic turn through planning, inspection, a failing test,
 * the file edit, verification, and the final assistant summary.
 */
const simulate = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  stopStream?.();
  const runId = Date.now();
  let stopped = false;
  const pendingDelays = new Map<ReturnType<typeof setTimeout>, (continueRun: boolean) => void>();
  const activeMessages = new Set<string>();
  const activeTools = new Set<string>();
  const activeReasoning = new Map<string, Set<string>>();

  const wait = async (milliseconds: number): Promise<boolean> => new Promise((resolve) => {
    if (stopped) {
      resolve(false);
      return;
    }
    const timeout = setTimeout(() => {
      pendingDelays.delete(timeout);
      resolve(!stopped);
    }, milliseconds);
    pendingDelays.set(timeout, resolve);
  });
  const waitForMessageList = async (): Promise<void> => {
    await chat.updateComplete;
    const list = chat.shadowRoot?.querySelector('dees-harness-message-list') as (
      HTMLElement & { updateComplete: Promise<unknown> }
    ) | null;
    if (list) await list.updateComplete;
  };
  const pushMessage = async (message: IHarnessMessage): Promise<boolean> => {
    chat.messages = [...chat.messages, message];
    if (message.streaming) activeMessages.add(message.id);
    if (message.toolCall?.status === 'pending' || message.toolCall?.status === 'running') {
      activeTools.add(message.id);
    }
    await waitForMessageList();
    if (!stopped) return true;
    if (message.streaming) {
      message.streaming = false;
      message.error ??= 'Simulation stopped.';
    }
    if (message.toolCall?.status === 'pending' || message.toolCall?.status === 'running') {
      message.toolCall.status = 'error';
      message.toolCall.errorText = 'Simulation stopped.';
      message.toolCall.finishedAt = Date.now();
    }
    chat.messages = [...chat.messages];
    return false;
  };
  const updateTool = (messageId: string, tool: Partial<IHarnessToolCall> & { id: string }) => {
    chat.applyDelta({ type: 'tool-update', messageId, tool });
    if (tool.status === 'completed' || tool.status === 'error') activeTools.delete(messageId);
  };
  const startReasoning = (messageId: string, partId: string): void => {
    chat.applyDelta({ type: 'reasoning-start', messageId, partId, startedAt: Date.now() });
    const parts = activeReasoning.get(messageId) ?? new Set<string>();
    parts.add(partId);
    activeReasoning.set(messageId, parts);
  };
  const endReasoning = (messageId: string, partId: string): void => {
    chat.applyDelta({ type: 'reasoning-end', messageId, partId, endedAt: Date.now() });
    const parts = activeReasoning.get(messageId);
    parts?.delete(partId);
    if (!parts?.size) activeReasoning.delete(messageId);
  };
  const finishMessage = (
    messageId: string,
    options: { usage?: IHarnessMessage['usage']; error?: string } = {},
  ): void => {
    chat.applyDelta({
      type: 'message-end',
      messageId,
      usage: options.usage,
      error: options.error,
    });
    activeMessages.delete(messageId);
  };
  const streamWords = async (
    messageId: string,
    partId: string,
    text: string,
  ): Promise<boolean> => {
    for (const word of text.split(' ')) {
      if (!await wait(38)) return false;
      chat.applyDelta({ type: 'reasoning', messageId, partId, delta: `${word} ` });
    }
    return true;
  };
  const streamText = async (messageId: string, text: string): Promise<boolean> => {
    for (let offset = 0; offset < text.length; offset += 8) {
      if (!await wait(32)) return false;
      chat.applyDelta({ type: 'text', messageId, delta: text.slice(offset, offset + 8) });
    }
    return true;
  };

  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    for (const [timeout, resolve] of pendingDelays) {
      clearTimeout(timeout);
      resolve(false);
    }
    pendingDelays.clear();
    for (const [messageId, parts] of [...activeReasoning]) {
      for (const partId of [...parts]) endReasoning(messageId, partId);
    }
    for (const messageId of [...activeMessages]) {
      finishMessage(messageId, { error: 'Simulation stopped.' });
    }
    for (const messageId of [...activeTools]) {
      const message = chat.messages.find((candidate) => candidate.id === messageId);
      if (!message?.toolCall) continue;
      updateTool(messageId, {
        id: message.toolCall.id,
        status: 'error',
        errorText: 'Simulation stopped.',
        finishedAt: Date.now(),
      });
    }
    chat.status = { type: 'idle' };
    if (stopStream === stop) stopStream = undefined;
  };
  stopStream = stop;

  const run = async (): Promise<void> => {
    chat.status = { type: 'busy', message: 'Planning…' };
    const reasoningId = `sim-${runId}-reasoning`;
    if (!await pushMessage({
      id: reasoningId,
      role: 'assistant',
      text: '',
      createdAt: Date.now(),
      streaming: true,
      model: 'gpt-5.5',
      effort: 'high',
    })) return;
    startReasoning(reasoningId, 'analysis');
    if (!await streamWords(
      reasoningId,
      'analysis',
      'The locale list participates in the cache key, so a rollout invalidates every route even when the manifest is unchanged.',
    )) return;
    endReasoning(reasoningId, 'analysis');
    if (!await wait(160)) return;
    startReasoning(reasoningId, 'plan');
    if (!await streamWords(
      reasoningId,
      'plan',
      'I will inspect the job, pin the key to the route manifest, run the focused regression test, and update the task list.',
    )) return;
    endReasoning(reasoningId, 'plan');
    finishMessage(reasoningId);

    if (!await wait(260)) return;
    chat.status = { type: 'busy', message: 'Updating task list…' };
    const todoId = `sim-${runId}-todos`;
    if (!await pushMessage({
      id: todoId, role: 'tool', text: '', createdAt: Date.now(),
      toolCall: {
        id: `${todoId}-call`, name: 'todowrite', status: 'running',
        input: { todos: [
          { content: 'Pin sitemap cache key to route manifest hash', status: 'in_progress' },
          { content: 'Run the sitemap test suite', status: 'pending' },
          { content: 'Apply the cache-key patch', status: 'pending' },
          { content: 'Verify staging timings', status: 'pending' },
        ] },
        startedAt: Date.now(),
      },
    })) return;
    if (!await wait(520)) return;
    updateTool(todoId, { id: `${todoId}-call`, status: 'completed', finishedAt: Date.now() });

    if (!await wait(240)) return;
    chat.status = { type: 'busy', message: 'Inspecting the sitemap job…' };
    const readId = `sim-${runId}-read`;
    if (!await pushMessage({
      id: readId, role: 'tool', text: '', createdAt: Date.now(),
      toolCall: {
        id: `${readId}-call`, name: 'read_file', status: 'running',
        input: { path: '/srv/app/src/jobs/sitemap.job.ts', startLine: 1, endLine: 80 },
        startedAt: Date.now(),
      },
    })) return;
    if (!await wait(620)) return;
    updateTool(readId, {
      id: `${readId}-call`,
      status: 'completed',
      output: 'const cacheKey = hash(locales);\nconst ttl = 3600;\nawait cache.set(cacheKey, sitemap, ttl);',
      finishedAt: Date.now(),
    });

    if (!await wait(240)) return;
    chat.status = { type: 'busy', message: 'Running the regression test…' };
    const testId = `sim-${runId}-test`;
    if (!await pushMessage({
      id: testId, role: 'tool', text: '', createdAt: Date.now(),
      toolCall: {
        id: `${testId}-call`, name: 'run_command', status: 'running',
        input: { command: 'pnpm test --filter sitemap', cwd: '/srv/app' },
        startedAt: Date.now(),
      },
    })) return;
    if (!await wait(520)) return;
    updateTool(testId, {
      id: `${testId}-call`,
      status: 'running',
      output: { stdout: '13 tests passed\n1 test still running…' },
    });
    if (!await wait(620)) return;
    updateTool(testId, {
      id: `${testId}-call`, status: 'error', isError: true,
      output: { exitCode: 1, stdout: '13 tests passed', stderr: 'cache key changed after locale reorder' },
      errorText: 'Regression confirmed before applying the patch.',
      finishedAt: Date.now(),
    });

    if (!await wait(260)) return;
    chat.status = { type: 'busy', message: 'Applying the patch…' };
    const editId = `sim-${runId}-edit`;
    if (!await pushMessage({
      id: editId, role: 'tool', text: '', createdAt: Date.now(),
      toolCall: {
        id: `${editId}-call`, name: 'write_file', status: 'running',
        input: {
          path: '/srv/app/src/jobs/sitemap.job.ts',
          oldContent: 'const cacheKey = hash(locales);\nconst ttl = 3600;',
          content: 'const cacheKey = hash(routeManifest);\nconst ttl = 3600;',
        },
        startedAt: Date.now(),
      },
    })) return;
    if (!await wait(720)) return;
    updateTool(editId, { id: `${editId}-call`, status: 'completed', finishedAt: Date.now() });

    if (!await wait(240)) return;
    chat.status = { type: 'busy', message: 'Verifying the patch…' };
    const verifyId = `sim-${runId}-verify`;
    if (!await pushMessage({
      id: verifyId, role: 'tool', text: '', createdAt: Date.now(),
      toolCall: {
        id: `${verifyId}-call`, name: 'run_command', status: 'running',
        input: { command: 'pnpm test --filter sitemap', cwd: '/srv/app' },
        startedAt: Date.now(),
      },
    })) return;
    if (!await wait(760)) return;
    updateTool(verifyId, {
      id: `${verifyId}-call`, status: 'completed',
      output: { exitCode: 0, stdout: '14 tests passed\n0 failed' },
      finishedAt: Date.now(),
    });

    if (!await wait(220)) return;
    const todoDoneId = `sim-${runId}-todos-2`;
    if (!await pushMessage({
      id: todoDoneId, role: 'tool', text: '', createdAt: Date.now(),
      toolCall: {
        id: `${todoDoneId}-call`, name: 'todowrite', status: 'completed',
        input: { todos: [
          { content: 'Pin sitemap cache key to route manifest hash', status: 'completed' },
          { content: 'Run the sitemap test suite', status: 'completed' },
          { content: 'Apply the cache-key patch', status: 'completed' },
          { content: 'Verify staging timings', status: 'completed' },
        ] },
        startedAt: Date.now(), finishedAt: Date.now(),
      },
    })) return;

    if (!await wait(260)) return;
    chat.status = { type: 'busy', message: 'Responding…' };
    const summaryId = `sim-${runId}-summary`;
    if (!await pushMessage({
      id: summaryId,
      role: 'assistant',
      text: '',
      createdAt: Date.now(),
      streaming: true,
      model: 'gpt-5.5',
      effort: 'high',
    })) return;
    const summary = [
      'Patched `sitemap.job.ts` so the cache is keyed by the **route manifest hash** instead of locale ordering.',
      '',
      '### Verification',
      '',
      '- Confirmed the regression before the edit',
      '- Applied the one-line cache-key change',
      '- Re-ran the focused suite: **14/14 tests passed**',
      '- Completed the staging timing check',
      '',
      'Locale-only rollouts now reuse the existing sitemap cache.',
    ].join('\n');
    if (!await streamText(summaryId, summary)) return;
    finishMessage(summaryId, {
      usage: { inputTokens: 2480, outputTokens: 340, totalTokens: 2820 },
    });
    chat.status = { type: 'idle' };
    if (stopStream === stop) stopStream = undefined;
  };

  void run().catch((error) => {
    console.error('Harness chat demo simulation failed.', error);
    stop();
  });
};

const askQuestion = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  chat.questions = [
    ...chat.questions,
    {
      id: `question-${Date.now()}`,
      question: 'Which cache-key strategy should I roll out?',
      createdAt: Date.now(),
      options: [
        { label: 'Route manifest hash', description: 'Deterministic, invalidates on route changes' },
        { label: 'Content hash', description: 'Fine-grained, more churn' },
      ],
    },
  ];
};

const askPermission = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  chat.permissions = [...chat.permissions, { ...pendingPermission, id: `perm-${Date.now()}` }];
};

const toggleError = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  chat.status = chat.status.type === 'error'
    ? { type: 'idle' }
    : { type: 'error', message: 'The model endpoint returned 429 — retrying in 20s.' };
};

const resetChat = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  stopStream?.();
  chat.messages = [...harnessDemoConversation(), ...toolShowcase()];
  chat.permissions = [];
  chat.status = { type: 'idle' };
};

export const demoFunc = () => demoApp(html`
  <style>
    .chatDemo {
      display: grid;
      grid-template-rows: auto minmax(0, 1fr);
      min-height: 0;
      height: 100%;
      width: 100%;
      box-sizing: border-box;
      overflow: hidden;
    }
    .chatDemo .controls {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      padding: 0 0 12px;
    }
    .chatDemo .controls dees-button::part(button) {
      height: 28px;
      min-height: 28px;
      border-radius: var(--dees-radius-md);
    }
    .chatDemo .scenarioLabel {
      margin-right: auto;
      color: var(--dees-color-text-secondary);
      font-size: 12px;
      font-weight: 500;
    }
    .chatDemo dees-harness-chat {
      min-width: 0;
      min-height: 0;
      overflow: hidden;
      border: 1px solid var(--dees-color-border-subtle);
      border-radius: var(--dees-radius-2xl);
      box-shadow: var(--dees-shadow-lg);
    }
    @media (max-width: 600px) {
      .chatDemo .controls {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .chatDemo .scenarioLabel { grid-column: 1 / -1; margin-right: 0; }
      .chatDemo .controls dees-button::part(button) {
        width: 100%;
        min-width: 0;
        padding: 0 8px;
        font-size: 12px;
      }
    }
  </style>
  <div
    class="chatDemo"
    @harness-send=${(event: CustomEvent) => console.log('harness-send', event.detail)}
    @harness-permission-response=${(event: CustomEvent) => {
      console.log('harness-permission-response', event.detail);
      const chat = (event.target as HTMLElement).closest('.chatDemo')?.querySelector('dees-harness-chat');
      if (chat) {
        // answered cards stay in the transcript showing the outcome
        chat.permissions = chat.permissions.map((request) =>
          request.id === event.detail.requestId
            ? { ...request, response: event.detail.response, respondedAt: Date.now() }
            : request,
        );
      }
    }}
    @harness-question-response=${(event: CustomEvent) => {
      console.log('harness-question-response', event.detail);
      const chat = (event.target as HTMLElement).closest('.chatDemo')?.querySelector('dees-harness-chat');
      if (chat) {
        chat.questions = chat.questions.map((request) =>
          request.id === event.detail.requestId
            ? { ...request, response: event.detail.answers, respondedAt: Date.now() }
            : request,
        );
      }
    }}
    @harness-scratchpad-save=${(event: CustomEvent) => console.log('harness-scratchpad-save', event.detail)}
    @harness-session-intelligence-ask=${(event: CustomEvent) => {
      console.log('harness-session-intelligence-ask', event.detail);
    }}
    @harness-abort=${() => {
      stopStream?.();
      console.log('harness-abort');
    }}
  >
    <div class="controls" role="toolbar" aria-label="Demo controls">
      <span class="scenarioLabel">Agent workspace</span>
      <dees-button type="accent" @clicked=${simulate}>Simulate streaming</dees-button>
      <dees-button @clicked=${simulateSubagents}>Simulate subagents</dees-button>
      <dees-button @clicked=${askPermission}>Ask permission</dees-button>
      <dees-button @clicked=${askQuestion}>Ask a question</dees-button>
      <dees-button @clicked=${toggleError}>Toggle error status</dees-button>
      <dees-button type="ghost" @clicked=${resetChat}>Reset</dees-button>
    </div>
    <dees-harness-chat
      heading="Deploy assistant"
      subheading="acme-app · main · connected"
      model="gpt-5.5"
      reasoningEffort="high"
      mode="Building"
      .modeOptions=${['Planning', 'Building']}
      .modelOptions=${['gpt-5.5', 'gpt-5', 'o4-mini']}
      .effortOptions=${['xhigh', 'high', 'medium', 'low']}
      .toolbarActions=${([
        {
          id: 'refresh',
          label: 'Refresh conversation',
          iconName: 'lucide:RefreshCw',
          action: () => console.log('refresh conversation'),
        },
        {
          id: 'delete',
          label: 'Delete conversation',
          iconName: 'lucide:Trash2',
          disabled: true,
          action: () => console.log('delete conversation'),
        },
      ] satisfies IHarnessChatToolbarAction[])}
      .markdownWhileStreaming=${false}
      .usage=${{ inputTokens: 18_400, outputTokens: 3_100, totalTokens: 21_500 }}
      .sessionMetrics=${{
        lifetimeUsedTokens: 184_320,
        compactionCount: 2,
        tokensBeforeLatestCompaction: 112_840,
        currentContextTokens: 47_200,
        maxContextTokens: 128_000,
      }}
      .scratchpad=${{
        id: 'deploy-assistant-demo',
        text: '- Cache keys use the route manifest hash.\n- Release after staging verification.',
        revision: 4,
        updatedAt: Date.now() - 45_000,
        updatedBy: 'intelligence',
      }}
      .sessionIntelligenceEnabled=${true}
      .intelligenceExchanges=${[{
        id: 'intelligence-demo',
        question: 'What remains before release?',
        answer: 'Staging verification is the only remaining task.',
        status: 'completed',
        model: 'gpt-5.6-luna',
        createdAt: Date.now() - 30_000,
        completedAt: Date.now() - 28_000,
      }]}
      .messages=${[...harnessDemoConversation(), ...toolShowcase()]}
    ></dees-harness-chat>
  </div>
`);
